import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useCallback, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { useDebouncedCallback } from "use-debounce";
import styles from "./Header.module.scss";
import { GeneralBanner } from "../GeneralBanner/GeneralBanner";
import { GeneralBannerQueryResult } from "../../../../studio/sanity.types";
import { BannerContext } from "../../../main/layout/layout";

// Notify at most every 250ms while scrolling, matching the debounced scroll handler below
const subscribeToScroll = (onChange: () => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const handleScroll = () => {
    if (timeoutId !== undefined) return;
    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      onChange();
    }, 250);
  };
  window.addEventListener("scroll", handleScroll);
  return () => {
    clearTimeout(timeoutId);
    window.removeEventListener("scroll", handleScroll);
  };
};
const getIsScrolled = () => window.scrollY > 0;
const getServerIsScrolled = () => false;
export const MainHeader: React.FC<{
  children: ReactNode | ReactNode[];
  hideOnScroll: boolean;
  generalBannerConfig?: GeneralBannerQueryResult;
  alwaysShrink?: boolean;
}> = t0 => {
  const $ = _c(35);
  const {
    children,
    hideOnScroll,
    generalBannerConfig,
    alwaysShrink
  } = t0;
  const [bannerContext] = useContext(BannerContext);
  const isScrolled = useSyncExternalStore(subscribeToScroll, getIsScrolled, getServerIsScrolled);
  const navbarShrinked = alwaysShrink || isScrolled;
  const [navBarVisible, setNavBarVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  let t1;
  if ($[0] !== lastScrollPosition) {
    t1 = () => {
      if (typeof window !== "undefined") {
        if (window.scrollY > lastScrollPosition && window.scrollY > 160) {
          setNavBarVisible(false);
        } else {
          if (window.scrollY < lastScrollPosition - 300 || window.scrollY <= 160) {
            setNavBarVisible(true);
          }
        }
      }
    };
    $[0] = lastScrollPosition;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const navBarVisibleCheck = t1;
  let t2;
  if ($[2] !== hideOnScroll || $[3] !== navBarVisibleCheck) {
    t2 = () => {
      if (hideOnScroll) {
        navBarVisibleCheck();
      }
      setLastScrollPosition(window.scrollY);
    };
    $[2] = hideOnScroll;
    $[3] = navBarVisibleCheck;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      maxWait: 250
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const debounced = useDebouncedCallback(t2, 250, t3);
  let t4;
  if ($[6] !== debounced) {
    t4 = () => {
      if (typeof window !== "undefined") {
        window.addEventListener("scroll", debounced);
      }
    };
    $[6] = debounced;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t4);
  let containerClasses;
  if ($[8] !== navbarShrinked) {
    containerClasses = [styles.container];
    if (navbarShrinked) {
      containerClasses.push(styles.navbarShrinked);
    }
    $[8] = navbarShrinked;
    $[9] = containerClasses;
  } else {
    containerClasses = $[9];
  }
  let headerStackClasses;
  if ($[10] !== navBarVisible) {
    headerStackClasses = [styles.headerStack];
    if (!navBarVisible) {
      headerStackClasses.push(styles.headerHidden);
    }
    $[10] = navBarVisible;
    $[11] = headerStackClasses;
  } else {
    headerStackClasses = $[11];
  }
  const showGeneralBanner = Boolean(generalBannerConfig) && !bannerContext.generalBannerDismissed;
  let wrapperClasses;
  if ($[12] !== showGeneralBanner) {
    wrapperClasses = [styles.wrapper];
    if (showGeneralBanner) {
      wrapperClasses.push(styles.hasGeneralBanner);
    }
    $[12] = showGeneralBanner;
    $[13] = wrapperClasses;
  } else {
    wrapperClasses = $[13];
  }
  let spacerClasses;
  if ($[14] !== alwaysShrink || $[15] !== showGeneralBanner) {
    spacerClasses = [styles.spacer];
    if (showGeneralBanner) {
      spacerClasses.push(styles.hasGeneralBanner);
    }
    if (alwaysShrink) {
      spacerClasses.push(styles.alwaysShrink);
    }
    $[14] = alwaysShrink;
    $[15] = showGeneralBanner;
    $[16] = spacerClasses;
  } else {
    spacerClasses = $[16];
  }
  const t5 = wrapperClasses.join(" ");
  const t6 = spacerClasses.join(" ");
  let t7;
  if ($[17] !== t6) {
    t7 = <div aria-hidden="true" data-cy="header-spacer" className={t6} />;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = containerClasses.join(" ");
  const t9 = headerStackClasses.join(" ");
  let t10;
  if ($[19] !== generalBannerConfig || $[20] !== showGeneralBanner) {
    t10 = showGeneralBanner && generalBannerConfig && <GeneralBanner configuration={generalBannerConfig} />;
    $[19] = generalBannerConfig;
    $[20] = showGeneralBanner;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== children) {
    t11 = <div data-cy="header-navbar" className={styles.navbarWrapper}>{children}</div>;
    $[22] = children;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <div className={t9}>{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t8) {
    t13 = <div data-cy="header" className={t8}>{t12}</div>;
    $[28] = t12;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t5 || $[33] !== t7) {
    t14 = <div className={t5}>{t7}{t13}</div>;
    $[31] = t13;
    $[32] = t5;
    $[33] = t7;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};